import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../utils/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * ProjectManager Component
 * Allows employers to create and manage projects for VAs
 * VAs can view and filter timesheet entries by project
 * 
 * Features:
 * - Create new projects with name, description, due date
 * - View active projects for the contract
 * - Archive/complete projects
 * - Select projects when logging time
 */
const ProjectManager = ({ 
  contractData, 
  employerProfile,
  vaProfile, 
  isEmployer, 
  isVA,
  onProjectsLoaded 
}) => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [filterTab, setFilterTab] = useState('active'); // 'active', 'paused', 'completed'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { projectId, projectName }

  // Fetch projects for the contract
  const fetchProjects = useCallback(async () => {
    if (!contractData?.id) {
      console.warn('[ProjectManager] No contractData.id available:', { contractData });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[ProjectManager] Fetching projects for contract:', contractData.id);
      const { data, error: fetchError } = await supabase
        .from('projects')
        .select('*')
        .eq('contract_id', contractData.id)
        .in('status', ['active', 'paused', 'completed'])
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('[ProjectManager] Fetch error:', fetchError);
        throw fetchError;
      }

      setProjects(data || []);
      
      // Notify parent component
      if (onProjectsLoaded) {
        onProjectsLoaded(data || []);
      }

      console.log(`[ProjectManager] Loaded ${data?.length || 0} projects for contract ${contractData.id}`);
    } catch (err) {
      console.error('[ProjectManager] Error fetching projects:', err);
      setError(`Failed to load projects: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [contractData?.id, onProjectsLoaded]);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Create new project
  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    if (!contractData?.id) {
      console.error('[ProjectManager] Contract data missing:', {
        contractDataExists: !!contractData,
        contractId: contractData?.id,
        contractData
      });
      setError('Contract information is missing. Please refresh the page.');
      return;
    }

    console.log('[ProjectManager] Form validation passed. ContractData:', {
      id: contractData.id,
      employer_id: contractData.employer_id,
      va_id: contractData.va_id,
      isEmployer
    });

    setSubmitting(true);
    setError(null);

    try {
      console.log('[ProjectManager] Attempting to create project with contract_id:', contractData.id);
      
      if (!employerProfile?.id) {
        throw new Error('Employer profile not loaded. Please refresh the page.');
      }
      
      const projectData = {
        contract_id: contractData.id,
        employer_id: employerProfile.id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        due_date: formData.dueDate || null,
        status: 'active',  // Valid values: 'active', 'paused', 'completed'
        progress: 0,
        priority: 'medium'
      };

      console.log('[ProjectManager] Project data to insert:', projectData);

      const { data, error: createError } = await supabase
        .from('projects')
        .insert([projectData])
        .select();

      if (createError) {
        console.error('[ProjectManager] Create error details:', {
          code: createError.code,
          message: createError.message,
          details: createError.details,
          hint: createError.hint,
          contractId: contractData.id,
          fullError: createError
        });
        
        // Provide user-friendly error messages
        if (createError.code === '42501') {
          setError('Permission denied. You do not have access to create projects for this contract. Please ensure you are the employer for this VA.');
        } else {
          setError(`Failed to create project: ${createError.message}`);
        }
        throw createError;
      }

      console.log('[ProjectManager] Project created:', data?.[0]?.id);

      // Add new project to list
      setProjects([data[0], ...projects]);

      // Reset form
      setFormData({ name: '', description: '', dueDate: '' });
      setShowCreateForm(false);

      // Notify parent
      if (onProjectsLoaded) {
        onProjectsLoaded([data[0], ...projects]);
      }
    } catch (err) {
      console.error('[ProjectManager] Error creating project:', err);
      setError(`Failed to create project: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Update project status
  const handleUpdateProjectStatus = async (projectId, newStatus) => {
    try {
      const updates = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      // Set progress based on status
      if (newStatus === 'completed') {
        updates.progress = 100;
      }

      console.log('[ProjectManager] Sending status update:', { projectId, newStatus, updates });

      const { data, error: updateError } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select();

      if (updateError) {
        console.error('[ProjectManager] Detailed error:', { 
          code: updateError.code, 
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          newStatus,
          updates
        });
        throw updateError;
      }

      console.log('[ProjectManager] Project status updated:', { projectId, newStatus, data });

      // Update projects list
      const updatedProjects = projects.map(p => 
        p.id === projectId ? { ...p, ...updates } : p
      );
      setProjects(updatedProjects);

      if (onProjectsLoaded) {
        onProjectsLoaded(updatedProjects);
      }
    } catch (err) {
      console.error('[ProjectManager] Error updating project status:', err);
      setError(`Failed to update project: ${err.message}`);
    }
  };

  // Delete project
  const handleDeleteProject = async (projectId) => {
    try {
      console.log('[ProjectManager] Deleting project:', projectId);

      const { error: deleteError } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (deleteError) {
        console.error('[ProjectManager] Delete error:', deleteError);
        throw deleteError;
      }

      console.log('[ProjectManager] Project deleted:', projectId);

      // Remove from projects list
      const updatedProjects = projects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);

      if (onProjectsLoaded) {
        onProjectsLoaded(updatedProjects);
      }

      // Clear confirmation dialog
      setDeleteConfirm(null);
    } catch (err) {
      console.error('[ProjectManager] Error deleting project:', err);
      setError(`Failed to delete project: ${err.message}`);
      setDeleteConfirm(null);
    }
  };

  // Filter projects based on current tab
  const getFilteredProjects = () => {
    let filtered = projects;

    if (filterTab === 'active') {
      filtered = projects.filter(p => p.status === 'active');
    } else if (filterTab === 'paused') {
      filtered = projects.filter(p => p.status === 'paused');
    } else if (filterTab === 'completed') {
      filtered = projects.filter(p => p.status === 'completed');
    }

    return filtered;
  };

  // Get paginated projects
  const getPagedProjects = () => {
    const filtered = getFilteredProjects();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Get total pages for current filter
  const getTotalPages = () => {
    const filtered = getFilteredProjects();
    return Math.ceil(filtered.length / itemsPerPage);
  };

  // Complete project (legacy - now uses handleUpdateProjectStatus)
  const handleCompleteProject = async (projectId) => {
    await handleUpdateProjectStatus(projectId, 'completed');
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Check if project deadline is approaching
  const isDeadlineApproaching = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    const daysUntilDue = Math.floor((due - today) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 7 && daysUntilDue >= 0;
  };

  // Check if project is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };

  if (!isEmployer && !isVA) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Icon name="FolderOpen" size={20} className="text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Projects</h2>
          {projects.length > 0 && (
            <span className="ml-3 px-2.5 py-0.5 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
              {projects.length}
            </span>
          )}
        </div>

        {isEmployer && (
          <Button
            variant={showCreateForm ? 'secondary' : 'primary'}
            size="sm"
            iconName={showCreateForm ? 'X' : 'Plus'}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : 'Add Project'}
          </Button>
        )}
      </div>

      {/* Filter Tabs */}
      {projects.length > 0 && (
        <div className="mb-4 flex gap-2 border-b border-gray-200">
          {[
            { value: 'active', label: 'Active', icon: 'Play' },
            { value: 'paused', label: 'Paused', icon: 'Pause' },
            { value: 'completed', label: 'Completed', icon: 'Check' }
          ].map(tab => {
            const count = tab.value === 'active' 
              ? projects.filter(p => p.status === 'active').length
              : tab.value === 'paused'
              ? projects.filter(p => p.status === 'paused').length
              : projects.filter(p => p.status === 'completed').length;

            return (
              <button
                key={tab.value}
                onClick={() => {
                  setFilterTab(tab.value);
                  setCurrentPage(1); // Reset to first page when changing tabs
                }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  filterTab === tab.value
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
                {count > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full font-semibold">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <Icon name="AlertCircle" size={18} className="text-red-600 mr-2 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-700 ml-2"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Create Project Form */}
      {isEmployer && showCreateForm && (
        <form onSubmit={handleCreateProject} className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Website Redesign, Social Media Campaign"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the project scope and deliverables"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={submitting}
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateForm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Projects List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 text-sm mt-2">Loading projects...</p>
        </div>
      ) : getFilteredProjects().length === 0 ? (
        <div className="text-center py-8">
          <Icon name="FolderOpen" size={48} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">
            {isEmployer 
              ? `No ${filterTab} projects yet.` 
              : `No ${filterTab} projects assigned yet.`}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {getPagedProjects().map((project) => (
              <div 
                key={project.id} 
                className={`p-4 border rounded-lg transition-colors ${
                  project.status === 'completed'
                    ? 'border-green-200 bg-green-50'
                    : project.status === 'paused'
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                {/* Project Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className={`font-medium ${
                      project.status === 'completed'
                        ? 'text-gray-600 line-through'
                        : 'text-gray-900'
                    }`}>
                      {project.name}
                    </h3>
                    {project.description && (
                      <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                    )}
                  </div>
                  
                  {isEmployer && (
                    <div className="ml-2 flex gap-1">
                      {project.status !== 'paused' && (
                        <button
                          onClick={() => handleUpdateProjectStatus(project.id, 'paused')}
                          className="px-2 py-1 text-xs font-medium text-yellow-600 hover:bg-yellow-100 rounded transition-colors"
                          title="Pause project"
                        >
                          <Icon name="Pause" size={16} />
                        </button>
                      )}
                      {project.status !== 'completed' && (
                        <button
                          onClick={() => handleUpdateProjectStatus(project.id, 'completed')}
                          className="px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-100 rounded transition-colors"
                          title="Mark as completed"
                        >
                          <Icon name="Check" size={16} />
                        </button>
                      )}
                      {project.status !== 'active' && (
                        <button
                          onClick={() => handleUpdateProjectStatus(project.id, 'active')}
                          className="px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Reactivate project"
                        >
                          <Icon name="RotateCcw" size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm({ projectId: project.id, projectName: project.name })}
                        className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-100 rounded transition-colors"
                        title="Delete project"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Project Status Badge */}
                <div className="mb-2">
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                    project.status === 'active'
                      ? 'bg-blue-100 text-blue-800'
                      : project.status === 'paused'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                  </span>
                </div>

                {/* Project Meta */}
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                  {project.due_date && (
                    <div className="flex items-center">
                      <Icon 
                        name={isOverdue(project.due_date) ? 'AlertCircle' : 'Calendar'} 
                        size={14} 
                        className={`mr-1 ${
                          isOverdue(project.due_date) 
                            ? 'text-red-600' 
                            : isDeadlineApproaching(project.due_date)
                            ? 'text-yellow-600'
                            : 'text-gray-500'
                        }`}
                      />
                      <span className={`${
                        isOverdue(project.due_date) 
                          ? 'text-red-600 font-medium' 
                          : isDeadlineApproaching(project.due_date)
                          ? 'text-yellow-700 font-medium'
                          : ''
                      }`}>
                        Due: {formatDate(project.due_date)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Progress Bar (if applicable) */}
                {project.progress > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">Progress</span>
                      <span className="text-xs font-medium text-gray-900">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {getTotalPages() > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, getFilteredProjects().length)} of {getFilteredProjects().length} projects
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(getTotalPages(), prev + 1))}
                  disabled={currentPage === getTotalPages()}
                  iconName="ChevronRight"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info Section */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700">
          💡 <span className="font-medium">Tip:</span> {' '}
          {isEmployer 
            ? 'Create projects to organize work and help VAs track their tasks more effectively.' 
            : 'Select a project when logging time to keep your work organized.'}
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 mx-auto mb-4">
              <Icon name="AlertCircle" size={24} className="text-red-600" />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Delete Project?
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.projectName}</strong>? This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteProject(deleteConfirm.projectId)}
                className="flex-1 px-4 py-2 bg-red-600 rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManager;
